<template>
  <div class="modal-card">
    <b-loading :isFullPage="false" :active="isLoading" />
    <header class="modal-card-head">
      <span class="modal-card-title is-size-5 has-text-centered">{{
        General.FORWARD
      }}</span>
      <div class="close-button custom-pointer" @click="close">
        <b-icon icon="close" />
      </div>
    </header>
    <div class="modal-content-container">
      <ValidationObserver v-slot="{ handleSubmit }" tag="section">
        <section class="spacing-p-10">
          <ValidationProvider v-slot="{ errors }" rules="required">
            <b-field
              :type="errors.length ? 'is-refused' : ''"
              customClass="has-text-color-7"
              :label="General.ORGAN_UNIT"
            >
              <b-taginput
                ref="stakeHolderCapacity"
                v-model="stakeHolderCapacity"
                :data="hierarchyCapacities(filterCapacity)"
                autocomplete
                field="id"
                :placeholder="General.PLACEHOLDER.EMPTY_ORGAN_CAPACITY"
                rounded
                :hasCounter="false"
                class="has-tag-ellipsis"
                role="combobox"
                maxtags="1"
                @typing="setFilterCapacity"
                @input="option => setCapacity(option)"
              >
                <template slot-scope="props">
                  <div class="is-flex">
                    <span class="has-text-weight-semibold">
                      {{ props.option.entity.abbreviation }}
                    </span>
                    <span>{{
                      `/${
                        props.option.abbreviation.includes("/")
                          ? props.option.abbreviation.split("/")[1]
                          : props.option.abbreviation
                      } - ${props.option.description}`
                    }}</span>
                  </div>
                </template>
                <template #selected="{ tags }">
                  <b-tag
                    v-for="(tag, index) in tags"
                    :key="index"
                    rounded
                    closable
                    :aria-close-label="
                      `${General.ARIA_LABEL.DELETE_FIELD} ${tag.name}`
                    "
                    @close="$refs.stakeHolderCapacity.removeTag(index, $event)"
                  >
                    <div class="is-flex is-align-items-center">
                      <b-tooltip
                        :label="
                          isDestinationInternal(tag.entity.id)
                            ? General.INTERNAL_PROCESSING
                            : General.EXTERNAL_PROCESSING
                        "
                        :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
                        role="tooltip"
                        size="is-small"
                        position="is-right"
                        multilined
                      >
                        <b-icon
                          class="is-flex spacing-mx-5"
                          :icon="
                            isDestinationInternal(tag.entity.id)
                              ? 'alpha-i-circle'
                              : 'alpha-e-circle'
                          "
                          :type="
                            isDestinationInternal(tag.entity.id)
                              ? 'is-primary'
                              : 'is-warning'
                          "
                        />
                      </b-tooltip>
                      <b-tooltip
                        :label="tag.name"
                        :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
                        :size="isMobile ? 'is-small' : 'is-large'"
                        role="tooltip"
                        position="is-bottom"
                        multilined
                      >
                        <div class="tag-text is-flex">
                          <span class="has-text-weight-semibold">
                            {{ tag.entity.abbreviation }}
                          </span>
                          <span class="has-ellipsis">
                            {{
                              `/${
                                tag.abbreviation.includes("/")
                                  ? tag.abbreviation.split("/")[1]
                                  : tag.abbreviation
                              } - ${tag.description}`
                            }}
                          </span>
                        </div>
                      </b-tooltip>
                    </div>
                  </b-tag>
                </template>
                <template slot="empty">
                  <p v-if="!isLoading">
                    {{ General.MESSAGES.NO_RECORDS_FOUND.toUpperCase() }}
                  </p>
                  <b-loading v-else :isFullPage="false" :active="isLoading" />
                </template>
              </b-taginput>
              <template #message>
                <ErrorField :errors="errors" />
              </template>
            </b-field>
          </ValidationProvider>

          <span class="has-text-color-7 has-text-weight-semibold">{{
            General.JUSTIFICATION
          }}</span>
          <ValidationProvider v-slot="{ errors }" rules="required">
            <b-field :type="errors.length ? 'is-refused' : ''">
              <b-input
                v-model="justification"
                maxlength="500"
                type="textarea"
                iconClickable
              />
              <template #message>
                <ErrorField :errors="errors" />
              </template>
            </b-field>
          </ValidationProvider>

          <section class="custom-overflow mt-20">
            <table
              class="table has-text-black"
              :title="General.TEXTS.PROCESS_LIST"
              :aria-describedby="General.TEXTS.PROCESS_LIST"
            >
              <thead>
                <tr>
                  <th class="has-text-color-8 is-size-7">{{ General.NUP }}</th>
                  <th class="has-text-color-8 is-size-7">
                    {{ General.SUBJECT }}
                  </th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="(row, id) in listProcess" :key="id">
                  <td class="has-text-color-8 is-size-7">
                    {{ applyMaskNUP(row.nup) }}
                  </td>
                  <td>
                    <div class="has-text-color-8 is-size-7">
                      {{ row.subject_name }}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </section>
        </section>

        <footer class="modal-card-foot is-justify-content-flex-end">
          <div class="buttons">
            <b-button
              :type="isContrast ? 'is-white-dark' : 'is-primary'"
              @click="handleSubmit(forward)"
            >
              {{ General.FORWARD }}
            </b-button>
            <b-button
              :type="isContrast ? 'is-white-dark' : 'is-warning'"
              outlined
              @click="close"
            >
              {{ General.CANCEL }}
            </b-button>
          </div>
        </footer>
      </ValidationObserver>
    </div>
  </div>
</template>

<script>
import { General, App } from "@/utils/constants";
import { NUP, applyMask } from "@/utils/protocols";
import { mapGetters, mapActions } from "vuex";
import ModalGenericConfirmation from "@/views/App/ModalGenericConfirmation";

export default {
  name: "ModalForwardProcess",
  props: {
    listProcess: {
      required: true,
      type: Array
    },
    callback: {
      type: Function,
      default: () => null
    }
  },
  data() {
    return {
      General,
      App,
      justification: General.TEXTS.DEFAULT_FORWARD_TEXT,
      filterCapacity: "",
      stakeHolderCapacity: [],
      capacityId: null,
      isLoading: false
    };
  },
  computed: {
    ...mapGetters({
      user: "getCurrentUser",
      hierarchyCapacities: "getHierarchyCapacitiesFiltered",
      isContrast: "isContrast",
      isMobile: "isMobile"
    })
  },
  async beforeMount() {
    this.isLoading = true;
    await this.capacityHierarchyRequest();
    this.isLoading = false;
  },
  methods: {
    ...mapActions({
      capacityHierarchyRequest: "capacityHierarchyRequest",
      forwardProcess: "forwardProcess"
    }),
    async forward() {
      this.isLoading = true;
      const result = await this.forwardProcess(
        this.listProcess.map(process => ({
          process_id: process.id,
          observation: this.justification,
          to_capacity: this.capacityId
        }))
      );
      this.isLoading = false;

      this.close();

      if (result) {
        this.$buefy.modal.open({
          parent: this,
          component: ModalGenericConfirmation,
          props: {
            name: this.stakeHolderCapacity[0].name,
            message: General.TEXTS.FORWARD_PROCESS
          },
          hasModalCard: true,
          trapFocus: true
        });
      }
    },

    close() {
      this.callback();
      this.$parent.close();
    },

    applyMaskNUP(value) {
      return applyMask(NUP, value);
    },

    formatTo(origin) {
      return `${origin.entity.abbreviation}/${origin.abbreviation} - ${origin.description}`;
    },

    setFilterCapacity(text) {
      this.isLoading = true;
      this.filterCapacity = text;
      this.isLoading = false;
    },

    setCapacity(option) {
      if (option.length) {
        this.capacityId = option[0].id;
      } else {
        this.capacityId = null;
      }
    },

    isDestinationInternal(part) {
      return this.user.capacity.part_id === part;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables";
.close-button {
  z-index: 2;
}

.modal-card {
  display: flex;
  justify-content: space-between;
  max-height: 75vh;
  background-color: $color-1;
  @media screen and (min-width: 768px) {
    width: 55vw;
  }
}

.spacing-mt-5.columns {
  margin-left: 0px;
  margin-right: 10px;
}

.spacing-mb-20 {
  margin-bottom: 10px !important;
}

.custom-overflow {
  height: 110px;
  overflow-y: auto;
}

.modal-content-container {
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  justify-content: space-between;
  overflow: auto;
}

table {
  width: 100%;
  tbody tr:hover {
    background-color: transparent !important;
  }
}

.optional-label {
  font-size: 10px;
  font-weight: 300;
  color: $color-13;
}

.tag-text {
  max-width: 45vw;
  @media screen and (max-width: 768px) {
    max-width: 80vw;
  }
  @media screen and (max-width: 350px) {
    max-width: 70vw;
  }
}
</style>
